/**
 * hook for fetching metadoc data with automatic polling during processing
 * manages polling interval based on metadoc processing status
 */
import { useEffect } from "react";
import { useGetMetadocQuery } from "services/metadoc-api";

import { logMetadocDebug } from "../../utils/metadocDebugLogger";

type UseMetadocPollingArgs = {
  metadocId?: string;
  forcePolling?: boolean;
};

export const useMetadocPolling = ({
  metadocId,
  forcePolling = false,
}: UseMetadocPollingArgs) => {
  const parsedId = metadocId ? parseInt(metadocId, 10) : undefined;

  // fetch metadoc data when we have an ID (with polling when processing)
  const {
    data: metadoc,
    isLoading: isMetadocLoading,
    isFetching: isMetadocFetching,
    refetch: refetchMetadoc,
  } = useGetMetadocQuery(parsedId!, {
    skip: !parsedId,
  });
  const shouldPoll =
    !!parsedId &&
    !!metadoc &&
    (forcePolling ||
      (metadoc.status !== "completed" && metadoc.status !== "failed"));

  // poll for status updates when metadoc is processing
  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    logMetadocDebug("metadoc polling started", {
      metadocId: parsedId,
      status: metadoc.status,
      forcePolling,
    });

    const pollInterval = setInterval(() => {
      refetchMetadoc();
    }, 3000);

    return () => {
      clearInterval(pollInterval);
      logMetadocDebug("metadoc polling stopped", {
        metadocId: parsedId,
        status: metadoc.status,
        forcePolling,
      });
    };
  }, [parsedId, metadoc?.status, refetchMetadoc, forcePolling, shouldPoll]);

  return {
    metadoc,
    isMetadocLoading,
    isMetadocFetching,
    refetchMetadoc,
  };
};
